import * as THREE from "three";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { ShaderPass } from "three/addons/postprocessing/ShaderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import {
  SuntraceImageDepthPass,
  type SuntraceImageDepthAtmosphere,
} from "./SuntraceImageDepthPass";
import {
  adaptSuntraceExposure,
  createSuntraceImageDepthProfile,
  type SuntraceImageDepthProfile,
} from "./imageDepth";
import type { SuntraceColorGrade } from "./colorScript";
import type { RacePresentationSnapshot, SuntraceQuality } from "./types";

export const SUNTRACE_POST_GRAPH = [
  "scene",
  "image-depth",
  "bloom",
  "speed-finish",
  "output",
] as const;

export const SUNTRACE_POST_CONTRACT = {
  toneMapOwners: ["output"],
  imageDepthQualities: ["high"],
  depthFormat: THREE.DepthFormat,
  depthType: THREE.UnsignedIntType,
} as const;

export const SUNTRACE_ROUTE_GRADE_CONTRACT = {
  maximumLift: 0.025,
  minimumGamma: 0.96,
  maximumGamma: 1.04,
  minimumGain: 0.96,
  maximumGain: 1.06,
} as const;

export const SUNTRACE_SPEED_FINISH_CONTRACT = {
  edgeWarpMaximum: 0.0014,
} as const;

const edgeWarpSpeed = 0.0008;
const edgeWarpBoost = SUNTRACE_SPEED_FINISH_CONTRACT.edgeWarpMaximum - edgeWarpSpeed;

const speedFinishShader = {
  uniforms: {
    tDiffuse: { value: null },
    uSpeed: { value: 0 },
    uBoost: { value: 0 },
    uImpact: { value: 0 },
    uSlip: { value: 0 },
    uTime: { value: 0 },
    uAspect: { value: 1 },
    uMotion: { value: 1 },
    uPostLift: { value: new THREE.Vector3() },
    uPostGamma: { value: new THREE.Vector3(1, 1, 1) },
    uPostGain: { value: new THREE.Vector3(1, 1, 1) },
  },
  vertexShader: `
    varying vec2 vUv;

    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float uSpeed;
    uniform float uBoost;
    uniform float uImpact;
    uniform float uSlip;
    uniform float uTime;
    uniform float uAspect;
    uniform float uMotion;
    uniform vec3 uPostLift;
    uniform vec3 uPostGamma;
    uniform vec3 uPostGain;
    varying vec2 vUv;

    float hash(vec2 value) {
      return fract(sin(dot(value, vec2(127.1, 311.7))) * 43758.5453);
    }

    void main() {
      vec2 centered = vUv - 0.5;
      centered.x *= uAspect;
      float distanceFromCenter = length(centered);
      vec2 direction = normalize(centered + vec2(0.0001));
      float edgeFactor = smoothstep(0.32, 0.95, distanceFromCenter);
      float velocityWarp = (uSpeed * ${edgeWarpSpeed.toFixed(4)} + uBoost * ${edgeWarpBoost.toFixed(4)}) * edgeFactor * uMotion;
      vec2 warpedUv = vUv - direction * velocityWarp;
      vec3 color = texture2D(tDiffuse, warpedUv).rgb;
      float speedBlur = pow(smoothstep(0.36, 0.92, uSpeed), 1.2) *
        pow(edgeFactor, 1.5) *
        (0.0019 + uBoost * 0.001) * uMotion;
      vec3 blurNear = texture2D(tDiffuse, warpedUv - direction * speedBlur).rgb;
      vec3 blurFar = texture2D(tDiffuse, warpedUv - direction * speedBlur * 2.2).rgb;
      color = color * 0.7 + blurNear * 0.2 + blurFar * 0.1;
      color = pow(
        max(color + uPostLift, vec3(0.0)),
        vec3(1.0) / uPostGamma
      ) * uPostGain;
      float vignette = smoothstep(1.04, 0.34, distanceFromCenter);
      color *= mix(0.96, 1.0, vignette);
      float grain = hash(vUv * vec2(1733.0, 947.0) + uTime) - 0.5;
      color += grain * 0.002;
      gl_FragColor = vec4(color, 1.0);
    }
  `,
};

export type SuntracePostProcessor = {
  render: (deltaSeconds: number) => void;
  update: (
    snapshot: RacePresentationSnapshot,
    reducedMotion: boolean,
    colorGrade: SuntraceColorGrade,
    deltaSeconds: number,
  ) => void;
  setAtmosphere: (atmosphere: SuntraceImageDepthAtmosphere) => void;
  resize: (width: number, height: number, pixelRatio: number) => void;
  setQuality: (quality: SuntraceQuality) => SuntraceImageDepthProfile;
  getMetrics: () => SuntracePostMetrics;
  restoreContext: () => void;
  dispose: () => void;
};

export type SuntracePostMetrics = {
  imageDepthEnabled: boolean;
  imageDepthScale: number;
  toneMapOwners: readonly ["output"];
};

const createComposerTarget = (
  width: number,
  height: number,
): THREE.WebGLRenderTarget => {
  const depthTexture = new THREE.DepthTexture(
    width,
    height,
    SUNTRACE_POST_CONTRACT.depthType,
  );
  depthTexture.format = SUNTRACE_POST_CONTRACT.depthFormat;
  depthTexture.name = "SUNTRACE.PostProcessing.Depth";
  const target = new THREE.WebGLRenderTarget(width, height, {
    type: THREE.HalfFloatType,
    depthBuffer: true,
    stencilBuffer: false,
    depthTexture,
  });
  target.texture.name = "SUNTRACE.PostProcessing.Color";
  return target;
};

const clampVector = (
  target: THREE.Vector3,
  source: THREE.Vector3,
  minimum: number,
  maximum: number,
  fallback: number,
): void => {
  target.set(
    THREE.MathUtils.clamp(Number.isFinite(source.x) ? source.x : fallback, minimum, maximum),
    THREE.MathUtils.clamp(Number.isFinite(source.y) ? source.y : fallback, minimum, maximum),
    THREE.MathUtils.clamp(Number.isFinite(source.z) ? source.z : fallback, minimum, maximum),
  );
};

export const createSuntracePostProcessor = (
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  width: number,
  height: number,
): SuntracePostProcessor => {
  const composerTarget = createComposerTarget(width, height);
  const composer = new EffectComposer(renderer, composerTarget);
  const renderPass = new RenderPass(scene, camera);
  const imageDepthPass = new SuntraceImageDepthPass(
    camera,
    createSuntraceImageDepthProfile("high"),
  );
  const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(width, height),
    0.1,
    0.24,
    1.18,
  );
  const finishPass = new ShaderPass(speedFinishShader);
  const outputPass = new OutputPass();
  composer.addPass(renderPass);
  composer.addPass(imageDepthPass);
  composer.addPass(bloomPass);
  composer.addPass(finishPass);
  composer.addPass(outputPass);
  let currentWidth = width;
  let currentHeight = height;
  let currentPixelRatio = 1;
  let currentExposure = Number.isFinite(renderer.toneMappingExposure)
    ? renderer.toneMappingExposure
    : 1;
  let currentImageDepthProfile = createSuntraceImageDepthProfile("high");
  let disposed = false;

  const disposeComposerDepthTextures = (): void => {
    const depthTextures = new Set<THREE.DepthTexture>();
    if (composer.renderTarget1.depthTexture !== null) {
      depthTextures.add(composer.renderTarget1.depthTexture);
    }
    if (composer.renderTarget2.depthTexture !== null) {
      depthTextures.add(composer.renderTarget2.depthTexture);
    }
    for (const ownedDepthTexture of depthTextures) {
      ownedDepthTexture.dispose();
    }
  };

  return {
    render(deltaSeconds) {
      if (disposed) {
        return;
      }
      composer.render(deltaSeconds);
    },
    update(snapshot, reducedMotion, colorGrade, deltaSeconds) {
      if (disposed) {
        return;
      }
      const speedUniform = finishPass.uniforms.uSpeed;
      const boostUniform = finishPass.uniforms.uBoost;
      const impactUniform = finishPass.uniforms.uImpact;
      const slipUniform = finishPass.uniforms.uSlip;
      const timeUniform = finishPass.uniforms.uTime;
      const motionUniform = finishPass.uniforms.uMotion;
      const liftUniform = finishPass.uniforms.uPostLift;
      const gammaUniform = finishPass.uniforms.uPostGamma;
      const gainUniform = finishPass.uniforms.uPostGain;
      if (speedUniform !== undefined) {
        speedUniform.value = THREE.MathUtils.clamp(snapshot.speed / 88, 0, 1);
      }
      if (boostUniform !== undefined) {
        boostUniform.value = THREE.MathUtils.clamp(snapshot.boost, 0, 1);
      }
      if (impactUniform !== undefined) {
        impactUniform.value = THREE.MathUtils.clamp(snapshot.impact, 0, 1);
      }
      if (slipUniform !== undefined) {
        slipUniform.value = THREE.MathUtils.clamp(snapshot.slip, -1, 1);
      }
      if (timeUniform !== undefined) {
        timeUniform.value = snapshot.time;
      }
      if (motionUniform !== undefined) {
        motionUniform.value = reducedMotion ? 0 : 1;
      }
      if (liftUniform?.value instanceof THREE.Vector3) {
        clampVector(
          liftUniform.value,
          colorGrade.postLift,
          -SUNTRACE_ROUTE_GRADE_CONTRACT.maximumLift,
          SUNTRACE_ROUTE_GRADE_CONTRACT.maximumLift,
          0,
        );
      }
      if (gammaUniform?.value instanceof THREE.Vector3) {
        clampVector(
          gammaUniform.value,
          colorGrade.postGamma,
          SUNTRACE_ROUTE_GRADE_CONTRACT.minimumGamma,
          SUNTRACE_ROUTE_GRADE_CONTRACT.maximumGamma,
          1,
        );
      }
      if (gainUniform?.value instanceof THREE.Vector3) {
        clampVector(
          gainUniform.value,
          colorGrade.postGain,
          SUNTRACE_ROUTE_GRADE_CONTRACT.minimumGain,
          SUNTRACE_ROUTE_GRADE_CONTRACT.maximumGain,
          1,
        );
      }
      const presentationFrozen = snapshot.phase === "paused" || snapshot.phase === "finished";
      if (!presentationFrozen) {
        currentExposure = adaptSuntraceExposure(
          currentExposure,
          colorGrade.exposure,
          deltaSeconds,
        );
      }
      renderer.toneMappingExposure = currentExposure;
      bloomPass.strength = 0.045 + snapshot.boost * 0.05 + snapshot.drift * 0.01;
    },
    setAtmosphere(atmosphere) {
      if (disposed) {
        return;
      }
      imageDepthPass.setAtmosphere(atmosphere);
    },
    resize(nextWidth, nextHeight, pixelRatio) {
      if (disposed) {
        return;
      }
      currentWidth = Math.max(1, Math.round(nextWidth));
      currentHeight = Math.max(1, Math.round(nextHeight));
      currentPixelRatio = Math.max(0.5, Number.isFinite(pixelRatio) ? pixelRatio : 1);
      composer.setPixelRatio(currentPixelRatio);
      composer.setSize(currentWidth, currentHeight);
      const aspectUniform = finishPass.uniforms.uAspect;
      if (aspectUniform !== undefined) {
        aspectUniform.value = currentWidth / currentHeight;
      }
    },
    setQuality(quality) {
      const imageDepthProfile = createSuntraceImageDepthProfile(quality);
      if (disposed) {
        return imageDepthProfile;
      }
      imageDepthPass.setProfile(imageDepthProfile);
      currentImageDepthProfile = imageDepthProfile;
      bloomPass.enabled = quality !== "low";
      bloomPass.radius = quality === "high" ? 0.16 : 0.13;
      bloomPass.threshold = quality === "high" ? 1.35 : 1.42;
      return imageDepthProfile;
    },
    getMetrics() {
      return {
        imageDepthEnabled: currentImageDepthProfile.enabled,
        imageDepthScale: currentImageDepthProfile.resolutionScale,
        toneMapOwners: [...SUNTRACE_POST_CONTRACT.toneMapOwners],
      };
    },
    restoreContext() {
      if (disposed) {
        return;
      }
      disposeComposerDepthTextures();
      composer.reset(
        createComposerTarget(
          Math.max(1, Math.round(currentWidth * currentPixelRatio)),
          Math.max(1, Math.round(currentHeight * currentPixelRatio)),
        ),
      );
    },
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      disposeComposerDepthTextures();
      outputPass.dispose();
      finishPass.dispose();
      bloomPass.dispose();
      imageDepthPass.dispose();
      composer.dispose();
    },
  };
};
